// Marketing-Inhalte der Produktseiten. Quelle: www.svisit.ch (Preise, Features,
// NFC-Visitenkarten). Texte hier pflegen, wenn sich HubSpot ändert — die
// Produkt-IDs sind stabil und auf HubSpot sowie im Header hart verlinkt.
// Bilder sind gebündelt (nicht Cloudinary), damit die Seiten die
// Cloudinary-Abschaltung (S4) überleben.
import basicKarte from '@assets/images/produkte/basic-karte.webp';
import basicInHand from '@assets/images/produkte/basic-in-hand.webp';
import customKarte from '@assets/images/produkte/custom-karte.webp';
import demoprofilHandy from '@assets/images/produkte/demoprofil-handy.webp';
import kartenDesigner from '@assets/images/produkte/karten-designer.webp';
import wallet from '@assets/images/produkte/wallet.webp';
import businessMitarbeiter from '@assets/images/produkte/business-mitarbeiter.webp';

export const WEBSITE = 'https://www.svisit.ch';

// og:image der Listing-Seite
export const LISTING_IMAGE = basicKarte;

export const PRODUCT_IDS = {
  basic: '65db4f2519422cfaf902c453',
  custom: '65db4f9319422cfaf902c455',
};

// Shop-weite Fakten (svisit.ch/preise). Versandtarife entsprechen der
// Allow-List SHIPPING_RATES in utils/invoiceOrder.js (Standardversand 500 /
// Expressversand 1000).
export const SHOP_FACTS = {
  deliveryTime: '2–4 Werktage',
  shippingStandard: 'CHF 5',
  shippingExpress: 'CHF 10',
  printedIn: 'Gedruckt in St. Gallen',
  payment: 'Kreditkarte, TWINT und weitere Methoden über Stripe',
};

export const TRUST_ITEMS = [
  'Einmalig bezahlen, kein Abo',
  SHOP_FACTS.printedIn,
  `Lieferung in ${SHOP_FACTS.deliveryTime}`,
  'Kreditkarte & TWINT',
];

export const HOW_IT_WORKS = [
  {
    title: 'Digitales Profil erstellen',
    text: 'Erstelle dein Profil mit Kontaktdaten, Links, Social Media und Terminbuchung. Alles bleibt jederzeit änderbar – anders als auf Papier.',
  },
  {
    title: 'Physische NFC-Karte bestellen',
    text: `Wähle die Basic-Karte im sVisit-Design oder gestalte deine Custom-Karte im Karten-Designer. ${SHOP_FACTS.printedIn}, geliefert in ${SHOP_FACTS.deliveryTime}n.`,
  },
  {
    title: 'Karte scannen und Profil teilen',
    text: 'Karte ans Smartphone halten oder QR-Code scannen – dein Gegenüber sieht dein Profil und speichert dich mit einem Tipp als Kontakt.',
  },
];

// Vergleich Basic/Custom. `true` = Häkchen, `false` = Strich, String = Text.
export const COMPARISON = [
  { label: 'Digitales Profil mit unlimitierten Links', basic: true, custom: true },
  { label: 'QR-Code-Fallback auf der Karte', basic: true, custom: true },
  { label: 'Apple & Google Wallet', basic: true, custom: true },
  { label: 'Kontakt speichern (vCard mit Foto)', basic: true, custom: true },
  { label: 'Statistiken zu Aufrufen und Downloads', basic: true, custom: true },
  { label: 'Kartendesign', basic: 'sVisit-Design', custom: 'Dein Design' },
  { label: 'Karten-Designer im Browser', basic: false, custom: true },
  { label: 'Beidseitiger Druck', basic: false, custom: true },
];

export const TECH_SPECS = [
  { label: 'Format', value: 'Kreditkartenformat, 85.6 × 54 mm' },
  { label: 'Technologie', value: 'NFC-Chip ohne Batterie – funktioniert, solange die Karte intakt ist' },
  { label: 'Kompatibilität', value: 'iPhone (ab iPhone 7) und Android, keine App nötig' },
  { label: 'Fallback', value: 'QR-Code auf der Karte öffnet dasselbe Profil' },
  { label: 'Wallet', value: 'Apple Wallet und Google Wallet' },
  { label: 'Profil', value: 'Unlimitierte Links, jederzeit änderbar' },
  { label: 'Sicherheit', value: 'Auf dem Chip liegt nur der Link zu deinem Profil – keine sensiblen Daten' },
];

// `icon` wird in ProductDetail auf ein Heroicon abgebildet.
export const POLICIES = [
  {
    icon: 'banknotes',
    name: 'Keine laufenden Kosten',
    description:
      'Basic und Custom bezahlst du einmalig. Dein digitales Profil bleibt kostenlos – so lange du willst.',
  },
  {
    icon: 'mapPin',
    name: SHOP_FACTS.printedIn,
    description: `Wir drucken in St. Gallen und liefern in ${SHOP_FACTS.deliveryTime}n. Standardversand ${SHOP_FACTS.shippingStandard}, Expressversand ${SHOP_FACTS.shippingExpress}.`,
  },
  {
    icon: 'pencil',
    name: 'Jederzeit aktuell',
    description:
      'Neue Nummer, neuer Titel: Profil ändern, fertig. Kein Neudruck, keine Kosten.',
  },
  {
    icon: 'lock',
    name: 'Sicher',
    description:
      'Verschlüsselte Übertragung; auf dem Chip liegt nur der Link zu deinem Profil – keine sensiblen Daten.',
  },
];

export const FAQ = [
  {
    question: 'Funktioniert NFC mit jedem Smartphone?',
    answer:
      'Ja, nahezu alle modernen Smartphones unterstützen NFC – sowohl iPhones (ab iPhone 7) als auch Android-Geräte. Der Empfänger benötigt keine spezielle App.',
  },
  {
    question: 'Was passiert, wenn mein Gegenüber kein NFC hat?',
    answer:
      'Neben NFC bietet das digitale Profil auch einen QR-Code als Alternative. Zusätzlich kann der Link zum Profil direkt per Nachricht geteilt werden. So erreichst du jeden Kontakt.',
  },
  {
    question: 'Fallen laufende Kosten an?',
    answer:
      'Nein, für Basic und Custom bezahlst du einmalig, dein digitales Profil bleibt kostenlos – so lange du willst.',
  },
  {
    question: 'Was passiert, wenn sich meine Daten ändern?',
    answer:
      'Du änderst dein Profil online, die Karte bleibt gleich. Kein Neudruck, keine Kosten.',
  },
  {
    question: 'Kann ich später von Basic auf Custom wechseln?',
    answer:
      'Ja. Du bestellst eine Custom-Karte und verknüpfst sie mit deinem bestehenden Profil.',
  },
  {
    question: 'Welche Zahlungsarten gibt es?',
    answer: `${SHOP_FACTS.payment}, Rechnung auf Anfrage.`,
  },
  {
    question: 'Wie lange hält eine NFC-Visitenkarte?',
    answer:
      'NFC-Chips benötigen keine eigene Energiequelle und haben praktisch keine begrenzte Lebensdauer. Die Karte funktioniert so lange, wie sie physisch intakt ist – in der Regel viele Jahre.',
  },
  {
    question: 'Wie sicher ist die Technologie?',
    answer:
      'NFC funktioniert nur auf sehr kurze Distanz von wenigen Zentimetern. Ein unbeabsichtigtes Auslesen ist praktisch ausgeschlossen. Zudem wird auf dem Chip lediglich ein Link zum digitalen Profil gespeichert – keine sensiblen Daten.',
  },
  {
    question: 'Gibt es Mengenrabatt?',
    answer:
      'Ab zwei Personen lohnt sich sVisit Business: ein Firmenkonto, alle Karten im Corporate Design, zentral verwaltet und abgerechnet.',
  },
];

export const BUSINESS_TEASER = {
  title: 'sVisit Business – für Teams',
  tagline:
    'Ein Firmenkonto, alle Karten im Corporate Design, zentral verwaltet.',
  price: 'ab CHF 4.00 pro Nutzer und Monat',
  note: 'Jährlich: 2 Monate geschenkt. Physische Karten separat.',
  bullets: [
    'Team-Verwaltung mit Admins',
    'Zentrales Kartendesign',
    'Karten neu zuweisen',
    'Zentrale Abrechnung',
    'Günstiger ab 11 Nutzern',
  ],
  href: `${WEBSITE}/business`,
  demoHref: `${WEBSITE}/business/demo`,
  image: businessMitarbeiter,
  imageAlt: 'sVisit Business – Mitarbeiterverwaltung im Firmenkonto',
};

// Wiederverwendete Highlight-Sections
const PROFILE_HIGHLIGHT = {
  title: 'Dein Profil – alle Links an einem Ort',
  text: 'Kontaktdaten, Website, Social Media, Portfolio, Terminbuchung: Füge unbegrenzt viele Links hinzu und passe sie mit wenigen Klicks an. Farben, Hintergrund und Header gestaltest du nach deinen Wünschen. Dein Gegenüber speichert dich mit einem Tipp als Kontakt – als vCard mit Foto.',
  image: demoprofilHandy,
  imageAlt: 'Digitales sVisit-Profil auf dem Smartphone',
};

const WALLET_HIGHLIGHT = {
  title: 'Apple & Google Wallet',
  text: 'Dein Profil als Pass auf dem Handy – teilen, auch wenn die Karte zu Hause liegt. Der Pass zeigt deinen QR-Code, dein Gegenüber scannt ihn und landet auf deinem Profil.',
  image: wallet,
  imageAlt: 'sVisit-Pass in Apple Wallet und Google Wallet',
};

const CONTENT = {
  [PRODUCT_IDS.basic]: {
    key: 'basic',
    shortName: 'Basic',
    tagline: 'Der Einstieg mit dem hochwertigen sVisit-Design',
    intro:
      'Eine hochwertige Karte im Kreditkartenformat mit NFC-Chip – im sVisit-Design. Halte sie an ein Smartphone oder lass den QR-Code scannen, und dein digitales Profil öffnet sich. Ohne App, jederzeit änderbar.',
    cta: 'Basic bestellen',
    badge: null,
    features: [
      'Digitales Profil',
      'Unlimitierte Links',
      'QR-Code-Fallback',
      'Apple & Google Wallet',
    ],
    gallery: [
      { src: basicKarte, alt: 'sVisit Basic Karte – Vorder- und Rückseite' },
      { src: basicInHand, alt: 'sVisit Basic Karte in der Hand' },
      { src: demoprofilHandy, alt: 'Digitales sVisit-Profil auf dem Smartphone' },
      { src: wallet, alt: 'sVisit-Pass in Apple Wallet und Google Wallet' },
    ],
    highlights: [PROFILE_HIGHLIGHT, WALLET_HIGHLIGHT],
    printSpecs: null,
    crossSell: {
      title: 'Lieber dein eigenes Design?',
      text: 'Die Custom-Karte ist beidseitig personalisierbar – gestalte sie im Karten-Designer und verknüpfe sie mit deinem bestehenden Profil.',
      to: `/produkte/${PRODUCT_IDS.custom}`,
      cta: 'Custom-Karte ansehen',
    },
  },
  [PRODUCT_IDS.custom]: {
    key: 'custom',
    shortName: 'Custom',
    tagline: 'Beidseitig personalisierbar – dein Design, deine Karte',
    intro:
      'Deine NFC-Visitenkarte im eigenen Design: Vorder- und Rückseite gestaltest du direkt im Browser – mit Logo, Farben, Schriften und QR-Code. Alles aus Basic ist inklusive.',
    cta: 'Custom gestalten',
    badge: 'Beidseitig bedruckt',
    features: [
      'Alles aus Basic',
      'Karten-Designer im Browser',
      'Beidseitiger Druck',
      'Logo, Text, Symbole und QR-Code frei platzierbar',
    ],
    gallery: [
      { src: customKarte, alt: 'sVisit Custom Karte – individuell bedruckte Vorder- und Rückseite' },
      { src: kartenDesigner, alt: 'sVisit Karten-Designer im Browser' },
      { src: demoprofilHandy, alt: 'Digitales sVisit-Profil auf dem Smartphone' },
      { src: wallet, alt: 'sVisit-Pass in Apple Wallet und Google Wallet' },
    ],
    highlights: [
      {
        title: 'Karten-Designer – deine Karte, dein Design',
        text: 'Gestalte Vorder- und Rückseite direkt im Browser: Text, Logo, Symbole, Formen, Linien und QR-Code frei platzieren, Schriften und Farben passend zu deinem Corporate Design wählen. Vorlagen helfen beim Start, die Vorschau zeigt dir das Druckergebnis.',
        image: kartenDesigner,
        imageAlt: 'sVisit Karten-Designer im Browser',
      },
      PROFILE_HIGHLIGHT,
    ],
    printSpecs: [
      'Kreditkartenformat 85.6 × 54 mm, beidseitig bedruckt',
      'Randlos bedruckt – Druckdaten mit 600 dpi automatisch aus dem Designer',
      `${SHOP_FACTS.printedIn}, Lieferung in ${SHOP_FACTS.deliveryTime}n`,
    ],
    crossSell: {
      title: 'Kein eigenes Design nötig?',
      text: 'Die Basic-Karte im sVisit-Design bietet dasselbe digitale Profil, QR-Code-Fallback und Wallet – der schnelle Einstieg.',
      to: `/produkte/${PRODUCT_IDS.basic}`,
      cta: 'Basic-Karte ansehen',
    },
  },
};

// Unbekannte Produkte (z. B. künftig angelegte) fallen auf die DB-Felder zurück.
export const getProductContent = (product) =>
  CONTENT[product._id] ?? {
    key: 'generic',
    shortName: product.name,
    tagline: product.description,
    intro: null,
    cta: product.hasCustomization ? 'Individualisieren' : 'Zum Warenkorb hinzufügen',
    badge: null,
    features: [],
    gallery: [{ src: product.image, alt: product.name }],
    highlights: [],
    printSpecs: null,
    crossSell: null,
  };

export const formatPrice = (rappen) =>
  `CHF ${(rappen / 100).toFixed(2).replace(/\.00$/, '')}`;

// og:image / JSON-LD brauchen absolute URLs; Vite liefert gehashte Pfade.
export const absoluteUrl = (path) => new URL(path, window.location.origin).href;
